import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/ui/multiselect";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function UserStatusModal({
  open,
  onClose,
  selectedUser,
  statusData,
  loading,
  availableDistricts,
  handleDistrictStatusChange,
  handleAddDistricts,
  setStatusData,
  handleSaveStatus,
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit User Status</DialogTitle>
        </DialogHeader>

        {selectedUser && (
          <div className="space-y-6"> 
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-semibold text-gray-600">Name</Label>
                <p className="text-sm mt-1">{selectedUser.name}</p>
              </div>
              <div>
                <Label className="font-semibold text-gray-600">Email</Label>
                <p className="text-sm mt-1">{selectedUser.email}</p>
              </div>
              <div>
                <Label className="font-semibold text-gray-600">Phone</Label>
                <p className="text-sm mt-1">{selectedUser.phone}</p>
              </div>
              <div>
                <Label className="font-semibold text-gray-600">
                  Designation
                </Label>
                <p className="text-sm mt-1">{selectedUser.designation}</p>
              </div>
            </div>
 
            <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50">
              <div>
                <Label className="text-base font-semibold">
                  User Account Status
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  {statusData.is_active
                    ? "User can access the system."
                    : "Deactivating will set all district assignments to inactive."}
                </p>
              </div>
              <Switch
                checked={statusData.is_active}
                onCheckedChange={(checked) =>
                  setStatusData((prev) => ({ ...prev, is_active: checked }))
                }
              />
            </div>
 
            <div className="space-y-3">
              <Label className="text-base font-semibold">
                District Assignments
              </Label>

              {statusData.assigned_districts.length > 0 ? (
                statusData.assigned_districts.map((district) => (
                  <div
                    key={district.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <p className="font-medium">{district.name}</p>

                    <div className="flex items-center gap-4">
                      <select
                        value={district.status}
                        onChange={(e) =>
                          handleDistrictStatusChange(
                            district.id,
                            e.target.value
                          )
                        }
                        className="px-3 py-2 border rounded-md"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>

                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          district.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {district.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">
                  No districts assigned
                </p>
              )}
            </div>
 
            <div className="space-y-3">
              <Label className="text-base font-semibold">
                Assign Additional Districts
              </Label>

              {!statusData.is_active && (
                <Alert>
                  <AlertDescription>
                    User must be active to assign new districts.
                  </AlertDescription>
                </Alert>
              )}

              <MultiSelect
                options={availableDistricts}
                value={statusData.new_districts}
                onChange={handleAddDistricts}
                disabled={!statusData.is_active}
                placeholder="Select additional districts..."
              />

              {statusData.new_districts.length > 0 && (
                <p className="text-sm text-blue-600">
                  {statusData.new_districts.length} new district(s) selected
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSaveStatus} disabled={loading}>
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
